import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar, Clock, Video, Send, Check, X, Search, Plus, User as UserIcon,
  CheckCircle2, XCircle, Hourglass, Trash2, Mail, MapPin,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AuthContext } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TZ = 'America/Montevideo';

const ROL_STYLE = {
  profesor:   { label: 'Profesor',   chip: 'bg-blue-100 text-blue-700 border-blue-300',     ring: 'ring-blue-300' },
  estudiante: { label: 'Estudiante', chip: 'bg-emerald-100 text-emerald-700 border-emerald-300', ring: 'ring-emerald-300' },
  admin:      { label: 'Admin',      chip: 'bg-purple-100 text-purple-700 border-purple-300', ring: 'ring-purple-300' },
  pastor:     { label: 'Pastor',     chip: 'bg-amber-100 text-amber-700 border-amber-300',   ring: 'ring-amber-300' },
  graduado:   { label: 'Graduado',   chip: 'bg-slate-100 text-slate-700 border-slate-300',   ring: 'ring-slate-300' },
};

const rolStyle = (rol) => ROL_STYLE[(rol || '').toLowerCase()] || { label: rol || 'Usuario', chip: 'bg-gray-100 text-gray-600 border-gray-300', ring: 'ring-gray-300' };

const RolBadge = ({ rol }) => {
  const s = rolStyle(rol);
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide border ${s.chip}`}>
      {s.label}
    </span>
  );
};

const Avatar = ({ nombre, foto, rol, size = 32, getImageUrl }) => {
  const url = foto && getImageUrl ? getImageUrl(foto) : null;
  const s = rolStyle(rol);
  const inicial = (nombre || 'U').trim().slice(0, 1).toUpperCase();
  return (
    <div
      className={`rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold ring-2 ${s.ring}`}
      style={{ width: size, height: size, fontSize: Math.max(10, size / 2.6) }}
    >
      {url ? (
        <img src={url} alt={nombre || 'avatar'} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
      ) : (
        inicial
      )}
    </div>
  );
};

const fmtFecha = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-UY', {
      timeZone: TZ, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const estadoBadge = (estado) => {
  const map = {
    pendiente: { color: 'bg-amber-100 text-amber-700 border-amber-300', label: '⏳ Pendiente' },
    aceptada: { color: 'bg-emerald-100 text-emerald-700 border-emerald-300', label: '✅ Aceptada' },
    rechazada: { color: 'bg-red-100 text-red-700 border-red-300', label: '❌ Rechazada' },
    realizada: { color: 'bg-blue-100 text-blue-700 border-blue-300', label: '✔️ Realizada' },
    cancelada: { color: 'bg-gray-100 text-gray-600 border-gray-300', label: '🚫 Cancelada' },
  };
  const m = map[estado] || map.pendiente;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${m.color}`}>{m.label}</span>;
};

// Helper: ajusta la URL Daily para UX óptima en mobile (mismo patrón que ChatIncomingCall / Sala Docente)
const optimizeDailyUrlForMobile = (url) => {
  if (!url || typeof window === 'undefined') return url;
  try {
    const u = new URL(url);
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      if (!u.searchParams.has('showLocalVideo')) u.searchParams.set('showLocalVideo', 'true');
      if (!u.searchParams.has('showParticipantsBar')) u.searchParams.set('showParticipantsBar', 'false');
      if (!u.searchParams.has('showFullscreenButton')) u.searchParams.set('showFullscreenButton', 'false');
      if (!u.searchParams.has('layout')) u.searchParams.set('layout', 'active-speaker');
    }
    return u.toString();
  } catch (_e) {
    return url;
  }
};

const CitaCard = ({ cita, currentUserId, onAccept, onReject, onCancel, onJoin, focused, getImageUrl }) => {
  const soyInvitado = cita.invitado_id === currentUserId;
  const otroNombre = soyInvitado ? cita.solicitante_nombre : cita.invitado_nombre;
  const otroRol = soyInvitado ? cita.solicitante_rol : cita.invitado_rol;
  const otroFoto = soyInvitado ? cita.solicitante_foto : cita.invitado_foto;
  // El botón "Unirse" aparece SIEMPRE que la cita esté aceptada — sin restricción de horario.
  // La sala Daily se crea sin expiración (mismo patrón que Sala Docente).
  const puedeUnirse = cita.estado === 'aceptada' && !!cita.daily_room_url;

  return (
    <div
      className={`bg-white rounded-xl border-2 p-3 shadow-sm hover:shadow-md transition-all ${focused ? 'ring-2 ring-indigo-400 border-indigo-400' : 'border-gray-200'}`}
      data-testid={`cita-card-${cita.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar nombre={otroNombre} foto={otroFoto} rol={otroRol} size={36} getImageUrl={getImageUrl} />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-900 truncate">{otroNombre || 'Usuario'}</div>
            <div className="flex items-center gap-1 mt-0.5">
              <RolBadge rol={otroRol} />
              {soyInvitado && <span className="text-[9px] text-gray-500">te invitó</span>}
            </div>
          </div>
        </div>
        {estadoBadge(cita.estado)}
      </div>
      <div className="text-sm font-semibold text-gray-900 mb-1">{cita.titulo}</div>
      {cita.motivo && <div className="text-xs text-gray-600 mb-2 line-clamp-2">{cita.motivo}</div>}
      <div className="flex items-center gap-3 text-xs text-gray-500 mb-2.5">
        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtFecha(cita.fecha_hora_inicio)}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{cita.duracion_minutos} min</span>
      </div>

      {cita.estado === 'pendiente' && soyInvitado && (
        <div className="flex gap-1.5">
          <Button size="sm" className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onAccept(cita)} data-testid={`accept-${cita.id}`}>
            <Check className="w-3 h-3 mr-1" />Aceptar
          </Button>
          <Button size="sm" variant="outline" className="flex-1 h-8 text-xs text-red-600 border-red-300 hover:bg-red-50"
            onClick={() => onReject(cita)} data-testid={`reject-${cita.id}`}>
            <X className="w-3 h-3 mr-1" />Rechazar
          </Button>
        </div>
      )}
      {cita.estado === 'pendiente' && !soyInvitado && (
        <div className="flex gap-1.5">
          <span className="flex-1 text-[10px] text-amber-700 bg-amber-50 px-2 py-1.5 rounded text-center">
            <Hourglass className="w-3 h-3 inline mr-1" />Esperando respuesta
          </span>
          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-gray-400 hover:text-red-600"
            onClick={() => onCancel(cita)} data-testid={`cancel-${cita.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
      {cita.estado === 'aceptada' && (
        <div className="flex gap-1.5">
          {puedeUnirse ? (
            <Button size="sm" className="flex-1 h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
              onClick={() => onJoin(cita)} data-testid={`join-${cita.id}`}>
              <Video className="w-3 h-3 mr-1" />Unirse ahora
            </Button>
          ) : (
            <span className="flex-1 text-[10px] text-emerald-700 bg-emerald-50 px-2 py-1.5 rounded text-center">
              <CheckCircle2 className="w-3 h-3 inline mr-1" />Confirmada · preparando sala...
            </span>
          )}
          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-gray-400 hover:text-red-600"
            onClick={() => onCancel(cita)} data-testid={`cancel-${cita.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
      {cita.estado === 'rechazada' && cita.motivo_rechazo && (
        <div className="text-[10px] text-gray-500 italic bg-gray-50 px-2 py-1 rounded">
          Motivo: {cita.motivo_rechazo}
        </div>
      )}
      {cita.estado === 'realizada' && cita.daily_room_url && (
        <div className="text-[10px] text-blue-600 italic">Sala usada: {cita.daily_room_name}</div>
      )}
    </div>
  );
};

export default function Citas() {
  const { user, getImageUrl } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusCitaId = searchParams.get('cita_id');
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(null); // cita
  const [motivoRechazo, setMotivoRechazo] = useState('');

  // Form crear cita
  const [invitables, setInvitables] = useState([]);
  const [searchInv, setSearchInv] = useState('');
  const [rolFiltro, setRolFiltro] = useState('todos'); // todos | profesor | estudiante | admin
  const [selInvitado, setSelInvitado] = useState(null);
  const [activeRoom, setActiveRoom] = useState(null); // {url, titulo} cuando estás dentro de la sala Daily embebida
  const [form, setForm] = useState({
    titulo: '', motivo: '', fecha: '', hora: '', duracion: 30,
  });
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const cargarCitas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/citas`, { headers });
      setCitas(res.data?.citas || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando citas');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { cargarCitas(); }, [cargarCitas]);

  const abrirCrear = async () => {
    setShowCreateModal(true);
    setSelInvitado(null);
    setSearchInv('');
    setRolFiltro('todos');
    setForm({ titulo: '', motivo: '', fecha: '', hora: '', duracion: 30 });
    try {
      const res = await axios.get(`${API}/citas/invitables`, { headers });
      setInvitables(res.data?.invitables || []);
    } catch (e) {
      toast.error('No se pudo cargar la lista de personas');
    }
  };

  const crearCita = async () => {
    if (!selInvitado) { toast.error('Elegí a quién invitar'); return; }
    if (!form.titulo.trim() || form.titulo.trim().length < 3) { toast.error('Indicá un título'); return; }
    if (!form.fecha || !form.hora) { toast.error('Indicá fecha y hora'); return; }
    // Combinar fecha+hora como TZ Montevideo (UTC-3)
    const iso = `${form.fecha}T${form.hora}:00-03:00`;
    setCreating(true);
    try {
      await axios.post(`${API}/citas`, {
        invitado_id: selInvitado.id,
        titulo: form.titulo,
        motivo: form.motivo,
        fecha_hora_inicio: iso,
        duracion_minutos: parseInt(form.duracion) || 30,
        organizador_tz: TZ,
      }, { headers });
      toast.success(`✅ Solicitud enviada a ${selInvitado.nombre_completo}`);
      setShowCreateModal(false);
      cargarCitas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error creando cita');
    } finally {
      setCreating(false);
    }
  };

  const aceptar = async (cita) => {
    try {
      await axios.post(`${API}/citas/${cita.id}/responder`, { aceptar: true }, { headers });
      toast.success('✅ Cita aceptada — sala de video creada');
      cargarCitas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error aceptando');
    }
  };

  const rechazar = async () => {
    if (!showRejectModal) return;
    try {
      await axios.post(`${API}/citas/${showRejectModal.id}/responder`,
        { aceptar: false, motivo_rechazo: motivoRechazo }, { headers });
      toast.info('Cita rechazada');
      setShowRejectModal(null);
      setMotivoRechazo('');
      cargarCitas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error rechazando');
    }
  };

  const cancelar = async (cita) => {
    if (!window.confirm(`¿Cancelar la cita "${cita.titulo}"?`)) return;
    try {
      await axios.post(`${API}/citas/${cita.id}/cancelar`, {}, { headers });
      toast.success('Cita cancelada');
      cargarCitas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cancelando');
    }
  };

  const unirse = (cita) => {
    if (cita.daily_room_url) {
      // Embebemos la sala Daily in-app (mismo patrón que Sala Docente)
      // Al cerrar el iframe, el usuario vuelve naturalmente a IBA Connect.
      setActiveRoom({
        url: optimizeDailyUrlForMobile(cita.daily_room_url),
        titulo: cita.titulo || 'Reunión',
      });
    }
  };

  // Filtros por columna
  const enviadas = citas.filter((c) => c.estado === 'pendiente');
  const aceptadas = citas.filter((c) => c.estado === 'aceptada');
  const realizadas = citas.filter((c) => ['realizada', 'rechazada', 'cancelada'].includes(c.estado));

  const filtroInvitables = invitables.filter((u) => {
    const q = searchInv.trim().toLowerCase();
    const rolU = (u.rol || '').toLowerCase();
    if (rolFiltro !== 'todos' && rolU !== rolFiltro) return false;
    if (!q) return true;
    return (u.nombre_completo || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || rolU.includes(q);
  });

  // Conteos por rol para los chips del filtro
  const conteoRol = invitables.reduce((acc, u) => {
    const r = (u.rol || '').toLowerCase();
    acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, {});

  // Si estamos dentro de la sala Daily, render full-screen iframe (igual que Sala Docente)
  if (activeRoom) {
    return (
      <div
        className="fixed inset-0 z-[9998] bg-black flex flex-col"
        style={{ height: '100dvh', maxHeight: '100dvh' }}
      >
        <div className="flex items-center justify-between px-3 py-2 bg-gray-900 shrink-0">
          <div className="flex items-center gap-2 text-white min-w-0">
            <Video className="w-4 h-4 shrink-0" />
            <span className="text-sm font-medium truncate">🎥 {activeRoom.titulo}</span>
          </div>
          <button
            onClick={() => setActiveRoom(null)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shrink-0 ml-2"
            data-testid="cita-leave-btn"
          >
            <X className="w-4 h-4" />
            <span>Salir</span>
          </button>
        </div>
        <iframe
          src={activeRoom.url}
          allow="camera *; microphone *; fullscreen; speaker; display-capture; autoplay; clipboard-write; picture-in-picture"
          allowFullScreen
          className="flex-1 w-full border-0"
          style={{ minHeight: 0 }}
          title="Daily Cita"
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-7xl" data-testid="citas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-7 h-7 text-indigo-600" />
            Solicitud Reunión
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Solicitá una reunión 1-a-1 por video con profesores, estudiantes o admins.
          </p>
        </div>
        <Button onClick={abrirCrear} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-crear-cita">
          <Plus className="w-4 h-4 mr-2" />Solicitar Reunión
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Cargando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Columna Enviadas */}
          <div className="bg-amber-50/40 rounded-xl border border-amber-200/60 p-3" data-testid="col-enviadas">
            <h2 className="font-semibold text-amber-900 mb-3 flex items-center gap-2 sticky top-0 bg-amber-50/40 py-1">
              <Hourglass className="w-4 h-4" />
              Enviadas / Pendientes
              <span className="ml-auto text-xs bg-amber-200 text-amber-900 rounded-full px-2 py-0.5">{enviadas.length}</span>
            </h2>
            <div className="space-y-2 max-h-[calc(100dvh-280px)] overflow-y-auto">
              {enviadas.length === 0 && (
                <p className="text-xs text-gray-500 text-center py-4">Sin citas pendientes</p>
              )}
              {enviadas.map((c) => (
                <CitaCard key={c.id} cita={c} currentUserId={user?.id}
                  onAccept={aceptar} onReject={(c) => setShowRejectModal(c)}
                  onCancel={cancelar} onJoin={unirse} focused={c.id === focusCitaId} getImageUrl={getImageUrl} />
              ))}
            </div>
          </div>

          {/* Columna Programadas */}
          <div className="bg-emerald-50/40 rounded-xl border border-emerald-200/60 p-3" data-testid="col-aceptadas">
            <h2 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2 sticky top-0 bg-emerald-50/40 py-1">
              <CheckCircle2 className="w-4 h-4" />
              Programadas
              <span className="ml-auto text-xs bg-emerald-200 text-emerald-900 rounded-full px-2 py-0.5">{aceptadas.length}</span>
            </h2>
            <div className="space-y-2 max-h-[calc(100dvh-280px)] overflow-y-auto">
              {aceptadas.length === 0 && (
                <p className="text-xs text-gray-500 text-center py-4">No tenés citas confirmadas</p>
              )}
              {aceptadas.map((c) => (
                <CitaCard key={c.id} cita={c} currentUserId={user?.id}
                  onAccept={aceptar} onReject={(c) => setShowRejectModal(c)}
                  onCancel={cancelar} onJoin={unirse} focused={c.id === focusCitaId} getImageUrl={getImageUrl} />
              ))}
            </div>
          </div>

          {/* Columna Realizadas */}
          <div className="bg-blue-50/40 rounded-xl border border-blue-200/60 p-3" data-testid="col-realizadas">
            <h2 className="font-semibold text-blue-900 mb-3 flex items-center gap-2 sticky top-0 bg-blue-50/40 py-1">
              <CheckCircle2 className="w-4 h-4" />
              Realizadas / Historial
              <span className="ml-auto text-xs bg-blue-200 text-blue-900 rounded-full px-2 py-0.5">{realizadas.length}</span>
            </h2>
            <div className="space-y-2 max-h-[calc(100dvh-280px)] overflow-y-auto">
              {realizadas.length === 0 && (
                <p className="text-xs text-gray-500 text-center py-4">Sin historial</p>
              )}
              {realizadas.map((c) => (
                <CitaCard key={c.id} cita={c} currentUserId={user?.id}
                  onAccept={aceptar} onReject={(c) => setShowRejectModal(c)}
                  onCancel={cancelar} onJoin={unirse} focused={c.id === focusCitaId} getImageUrl={getImageUrl} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Cita */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="modal-crear-cita">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Solicitar Reunión
            </DialogTitle>
            <DialogDescription>
              Elegí a quién querés invitar, fecha y hora. Le va a llegar una notificación para aceptar o rechazar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>¿A quién invitás?</Label>
              {!selInvitado ? (
                <>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      value={searchInv}
                      onChange={(e) => setSearchInv(e.target.value)}
                      placeholder="Buscar por nombre, email o rol..."
                      className="pl-9"
                      data-testid="search-invitable"
                    />
                  </div>
                  {/* Chips filtro por rol */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[
                      { key: 'todos', label: `Todos (${invitables.length})`, cls: 'bg-gray-100 text-gray-700 border-gray-300' },
                      { key: 'profesor', label: `Profesores (${conteoRol.profesor || 0})`, cls: 'bg-blue-100 text-blue-700 border-blue-300' },
                      { key: 'estudiante', label: `Estudiantes (${conteoRol.estudiante || 0})`, cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                      { key: 'admin', label: `Admins (${conteoRol.admin || 0})`, cls: 'bg-purple-100 text-purple-700 border-purple-300' },
                    ].filter((c) => c.key === 'todos' || (conteoRol[c.key] || 0) > 0).map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setRolFiltro(c.key)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition ${rolFiltro === c.key ? `${c.cls} ring-2 ring-offset-1 ring-indigo-400` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                        data-testid={`filtro-rol-${c.key}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 max-h-56 overflow-y-auto border rounded-lg divide-y">
                    {filtroInvitables.length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-3">Sin resultados</p>
                    )}
                    {filtroInvitables.slice(0, 60).map((u) => (
                      <button key={u.id} type="button"
                        onClick={() => setSelInvitado(u)}
                        className="w-full px-3 py-2 hover:bg-indigo-50 flex items-center gap-2 text-left transition"
                        data-testid={`pick-${u.id}`}
                      >
                        <Avatar nombre={u.nombre_completo || u.email} foto={u.foto_perfil} rol={u.rol} size={32} getImageUrl={getImageUrl} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                            <span className="truncate">{u.nombre_completo}</span>
                            <RolBadge rol={u.rol} />
                          </div>
                          <div className="text-xs text-gray-500 truncate">{u.email}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="mt-1 flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <Avatar nombre={selInvitado.nombre_completo} foto={selInvitado.foto_perfil} rol={selInvitado.rol} size={44} getImageUrl={getImageUrl} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
                      <span className="truncate">{selInvitado.nombre_completo}</span>
                      <RolBadge rol={selInvitado.rol} />
                    </div>
                    <div className="text-xs text-gray-500 truncate">{selInvitado.email}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setSelInvitado(null)} className="h-7 px-2">
                    Cambiar
                  </Button>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="titulo">Título de la reunión *</Label>
              <Input id="titulo" value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder='Ej: "Tutoría sobre Lección 5"' className="mt-1"
                data-testid="cita-titulo" />
            </div>

            <div>
              <Label htmlFor="motivo">Motivo (opcional)</Label>
              <Textarea id="motivo" value={form.motivo}
                onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                placeholder="Detalle breve del tema a tratar"
                rows={2} className="mt-1"
                data-testid="cita-motivo" />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="fecha">Fecha *</Label>
                <Input id="fecha" type="date" value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className="mt-1" data-testid="cita-fecha" />
              </div>
              <div>
                <Label htmlFor="hora">Hora * (UY)</Label>
                <Input id="hora" type="time" value={form.hora}
                  onChange={(e) => setForm({ ...form, hora: e.target.value })}
                  className="mt-1" data-testid="cita-hora" />
              </div>
              <div>
                <Label htmlFor="duracion">Duración</Label>
                <select id="duracion" value={form.duracion}
                  onChange={(e) => setForm({ ...form, duracion: e.target.value })}
                  className="mt-1 w-full h-10 px-2 rounded-md border border-input bg-background text-sm"
                  data-testid="cita-duracion">
                  <option value="15">15 min</option>
                  <option value="30">30 min</option>
                  <option value="45">45 min</option>
                  <option value="60">1 hora</option>
                  <option value="90">1.5 hora</option>
                </select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={creating}>
              Cancelar
            </Button>
            <Button onClick={crearCita} disabled={creating || !selInvitado}
              className="bg-indigo-600 hover:bg-indigo-700" data-testid="cita-enviar">
              <Send className="w-4 h-4 mr-2" />
              {creating ? 'Enviando...' : 'Enviar solicitud'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Rechazar */}
      <Dialog open={!!showRejectModal} onOpenChange={(o) => !o && setShowRejectModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <XCircle className="w-5 h-5" />Rechazar cita
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="motivo-rechazo">Motivo (opcional)</Label>
            <Textarea id="motivo-rechazo" value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              placeholder="Ej: tengo otro compromiso a esa hora"
              rows={3} className="mt-1"
              data-testid="motivo-rechazo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectModal(null); setMotivoRechazo(''); }}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={rechazar} data-testid="confirmar-rechazo">
              <X className="w-4 h-4 mr-2" />Rechazar cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
